import React, { useEffect, useState } from 'react';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  useTheme,
  Box
} from '@mui/material';
import { BRAND_TABLE_TEXT_COLOR } from '../styles/tableColors';

interface Column {
  label: string;
  key: string;
  align?: 'left' | 'right' | 'center';
  renderCell?: (row: Record<string, unknown>) => React.ReactNode;
  sortable?: boolean;
  /**
   * Optional accessor to provide a sortable value (e.g., timestamps for dates).
   * When provided, sorting uses this value instead of the raw cell content.
   */
  sortValue?: (row: Record<string, unknown>) => string | number | Date | null | undefined;
}

interface TableWithPaginationProps {
  columns: Column[];
  rows: Record<string, unknown>[];
  rowsPerPage?: number;
  getRowKey: (row: Record<string, unknown>, index?: number) => string | number;
  emptyMessage?: string;
  minTableWidth?: number | string;
}

const TableWithPagination: React.FC<TableWithPaginationProps> = ({
  columns,
  rows,
  rowsPerPage = 10,
  getRowKey,
  emptyMessage = 'No data available.',
  minTableWidth
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(columns.find(col => col.sortable)?.key || '');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (colKey: string) => {
    if (sortBy === colKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(colKey);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    setPage(1);
  }, [rows]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((rows?.length || 0) / rowsPerPage));
    if (page > maxPage) setPage(maxPage);
  }, [page, rows, rowsPerPage]);

  const sortedRows = React.useMemo(() => {
    if (!sortBy) return rows;

    const sortCol = columns.find(c => c.key === sortBy);
    const accessor = sortCol?.sortValue;

    const toComparable = (val: unknown) => {
      if (val === null || val === undefined) return null;
      if (val instanceof Date) return val.getTime();
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const parsed = Date.parse(val);
        if (!Number.isNaN(parsed)) return parsed;
        return val.toLowerCase();
      }
      return String(val);
    };

    return [...rows].sort((a, b) => {
      const rawA = accessor ? accessor(a) : a[sortBy];
      const rawB = accessor ? accessor(b) : b[sortBy];
      const valA = toComparable(rawA);
      const valB = toComparable(rawB);

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (valA === valB) return 0;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return sortDirection === 'asc' ? -1 : 1;
    });
  }, [rows, sortBy, sortDirection]);

  const pageCount = Math.ceil(sortedRows.length / rowsPerPage);
  const paginatedRows = sortedRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} sx={{ 
        width: '100%', 
        borderRadius: 4, 
        background: theme.palette.mode === 'dark' ? '#232228' : '#fff', 
        mt: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? '#444' : '#ddd'}`,
        overflowX: 'auto',
        overflowY: 'hidden'
      }}>
        <Table sx={{
          borderCollapse: 'collapse',
          minWidth: minTableWidth,
          '& td, & th': {
            border: `1px solid ${theme.palette.mode === 'dark' ? '#444' : '#ddd'}`
          }
        }}>
        <TableHead>
          <TableRow sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(205, 57, 238, 0.1)' : '#fbfbfb'
          }}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || 'left'}
                sx={{
                  color: BRAND_TABLE_TEXT_COLOR,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: col?.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  padding: '12px 24px',
                  gap: '12px'
                }}
                onClick={() => col?.sortable && handleSort(col.key)}
              >
                <Box>
                  {col.label}
                  {col.sortable && (
                    sortBy === col.key
                      ? (sortDirection === 'asc' 
                          ? <ArrowDropUpIcon fontSize="small" /> 
                          : <ArrowDropDownIcon fontSize="small" />)
                      : <ArrowDropUpIcon fontSize="small" sx={{ opacity: 0.3 }} /> // faded arrow for other sortable columns
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ color: BRAND_TABLE_TEXT_COLOR }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, idx) => (
                <TableRow 
                  key={getRowKey(row, (page - 1) * rowsPerPage + idx)}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sx={{
                        color: BRAND_TABLE_TEXT_COLOR,
                        padding: '12px 24px',
                        gap: '12px',
                        fontSize: '14px',
                        fontWeight: 400
                      }}
                    >
                      {col.renderCell ? col.renderCell(row) : String(row[col.key] || '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pageCount > 1 && (
        <Box display="flex" justifyContent="flex-end" mt={2} sx={{ '& .MuiPaginationItem-root': { borderColor: 'rgba(255,255,255,0.5)' } }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            shape="rounded"
            color="primary"
            showFirstButton
            showLastButton
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  borderRadius: '8px',
                  border: item.selected ? '1px solid #98773b' : '1px solid rgba(255,255,255,0.5)',
                  bgcolor: item.selected ? '#98773b !important' : '#fff',
                  color: item.selected ? '#fff' : '#333',
                  mx: 0.5,
                  minWidth: 36,
                  minHeight: 36,
                  fontWeight: 500,
                  fontSize: 16,
                  '& .MuiSvgIcon-root': { color: ['first', 'previous', 'next', 'last'].includes(item.type) ? (item.disabled ? 'rgba(0,0,0,0.26)' : '#333') : undefined },
                  '&:hover': {
                    color: '#fff',
                    '& .MuiSvgIcon-root': { color: '#fff' },
                  },
                }}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default TableWithPagination; 
